import { Button, Group, Modal, Pagination, Select, Stack, Table, Text } from "@mantine/core";
import { ConfirmNote, DisabledReason, EmptyStateGuide, HelpButtonGroup, i18n, nextStepToast } from "@dehub/theme";
import { BellIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { NotificationTemplateApi } from "../../../apis";
import type { INotificationTemplate, NotificationChannel } from "../../../apis/notification-template";
import { DeliveryRowAction } from "../../../components/delivery/action-controls";
import { DotPill, TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { TemplateEditorDrawer } from "../../../components/return-settings/template-editor-drawer";
import { DeniedCard, HelpColumnTitle, ListState, SegmentedTabs } from "../../../components/ui";
import { dateFormat } from "../../../utils/date";
import { message } from "../../../utils/message";

const PAGE_SIZE = 20;

/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.returns-settings-notification-templates";

// Lining figures so dates align down a column.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

// O169 — the channel enum is the BE NOTIFICATION_CHANNEL (SMS / EMAIL / PUSH).
export const NOTIFICATION_CHANNELS: NotificationChannel[] = ["SMS", "EMAIL", "PUSH"];

const breadcrumb = () => [
  { to: "/",               label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns",        label: i18n.t("returns:breadcrumb.returns") },
  { to: "/returns/settings", label: i18n.t("returns:breadcrumb.settings") },
  { label: i18n.t("returns:notifTemplatesPage.breadcrumb") },
];

type ActiveFilter = "ALL" | "ACTIVE" | "INACTIVE";

function activeFilterToParam(f: ActiveFilter): boolean | undefined {
  if (f === "ACTIVE")   return true;
  if (f === "INACTIVE") return false;
  return undefined;
}

export function NotificationTemplatesPage() {
  const { t } = useTranslation("returns");
  // BE notification-template: list/get = BIZ_GOV_VIEW, create/update/remove = BIZ_GOV_MNG.
  const view = usePermission("BIZ_GOV_VIEW");
  const manage = usePermission("BIZ_GOV_MNG");
  const manageState = { allowed: manage.hasAccess, reason: manage.reason };

  const [channelFilter, setChannelFilter] = useState("");
  const [activeFilter, setActiveFilter]   = useState<ActiveFilter>("ALL");
  const [page, setPage]                   = useState(1);

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [editTarget, setEditTarget]     = useState<INotificationTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<INotificationTemplate | null>(null);
  const [deleting, setDeleting]         = useState(false);

  const { data, error, isLoading, mutate } = useSWR(
    view.hasAccess ? ["notification-templates", channelFilter, activeFilter, page] : null,
    () => NotificationTemplateApi.list({
      filter: { channel: channelFilter || undefined, isActive: activeFilterToParam(activeFilter) },
      offset: { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE },
    }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );
  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilter = !!channelFilter || activeFilter !== "ALL";

  const channelOptions = [
    { value: "", label: t("trustList.all") },
    ...NOTIFICATION_CHANNELS.map((c) => ({ value: c, label: t(`templateEditor.channel.${c}`) })),
  ];
  const activeOptions = [
    { label: t("trustList.all"), value: "ALL" },
    { label: t("notifTemplatesPage.active"), value: "ACTIVE" },
    { label: t("notifTemplatesPage.inactive"), value: "INACTIVE" },
  ];

  const handleNew = () => { setEditTarget(null); setDrawerOpened(true); };
  const handleEdit = (template: INotificationTemplate) => { setEditTarget(template); setDrawerOpened(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await NotificationTemplateApi.remove(deleteTarget.id);
      nextStepToast(`${HELP}.toast.deleted`, { fallback: { title: t("notifTemplatesPage.deleted") } });
      setDeleteTarget(null);
      mutate();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t("notifTemplatesPage.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  // O176 — without BIZ_GOV_VIEW the page is a calm card with the code, not an empty table.
  if (!view.hasAccess) {
    return (
      <PageLayout title={t("notifTemplatesPage.title")} breadcrumb={breadcrumb()}>
        <DeniedCard permission="BIZ_GOV_VIEW" />
      </PageLayout>
    );
  }

  const footer = (
    <>
      <Text span style={TNUM}>{rows.length ? `${(page - 1) * PAGE_SIZE + 1}-${(page - 1) * PAGE_SIZE + rows.length} / ${total}` : `0 / ${total}`}</Text>
      {totalPages > 1 ? <Pagination value={page} total={totalPages} onChange={setPage} size="sm" /> : <Group />}
    </>
  );

  // Kept above the empty state too, so an empty list still shows its columns (Ord_ReturnTemplates).
  const thead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("preventionTable.actions")}</Table.Th>
        <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-template-key`}>{t("notifTemplatesPage.col.templateKey")}</HelpColumnTitle></Table.Th>
        <Table.Th>{t("notifTemplatesPage.channel")}</Table.Th>
        <Table.Th>{t("notifTemplatesPage.col.subject")}</Table.Th>
        <Table.Th><HelpColumnTitle helpKey={`${HELP}.field.col-active`}>{t("notifTemplatesPage.col.active")}</HelpColumnTitle></Table.Th>
        <Table.Th>{t("notifTemplatesPage.col.updated")}</Table.Th>
      </Table.Tr>
    </Table.Thead>
  )

  return (
    <PageLayout
      title={t("notifTemplatesPage.title")}
      subTitle={t("notifTemplatesPage.subtitle")}
      breadcrumb={breadcrumb()}
      extra={[
        // @dehub/help: attached «?» (R2) + reason popover without the permission (R4).
        <DisabledReason key="new" helpKey={`${HELP}.reason.manage`} reason="PERM" when={!manage.hasAccess} position="bottom-end">
          <HelpButtonGroup helpKey={`${HELP}.action.new`}>
            <Button data-help-id="returns-template-new" leftSection={<PlusIcon size={16} />} onClick={handleNew}>
              {t("notifTemplatesPage.newTemplate")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}
    >
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <Select
            aria-label={t("notifTemplatesPage.channel")}
            placeholder={t("notifTemplatesPage.channel")}
            data={channelOptions}
            value={channelFilter}
            onChange={(v) => { setChannelFilter(v ?? ""); setPage(1); }}
            allowDeselect={false}
            w={200}
          />
          <SegmentedTabs
            value={activeFilter}
            onChange={(v) => { setActiveFilter(v as ActiveFilter); setPage(1); }}
            data={activeOptions}
          />
        </Group>

        <TableFrame footer={rows.length ? footer : undefined}>
          {error && !data ? (
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : isLoading && !data ? (
            <ListState state="loading" columns={6} />
          ) : rows.length === 0 ? (
            <>
              <Table verticalSpacing="sm" horizontalSpacing="md">
                {thead}
              </Table>
            {/* @dehub/help: why the list is empty and what to do (R9); the plain states stay the fallback. */}
            <EmptyStateGuide
              helpKey={`${HELP}.empty.main`}
              when={hasFilter ? "noResults" : undefined}
              icon={<BellIcon size={24} />}
              actions={{
                clear: () => { setChannelFilter(""); setActiveFilter("ALL"); setPage(1); },
                create: () => { if (manage.hasAccess) handleNew(); },
              }}
              fallback={hasFilter ? (
                <ListState
                  state="noResults"
                  action={{ onClick: () => { setChannelFilter(""); setActiveFilter("ALL"); setPage(1); } }}
                />
              ) : (
                <ListState
                  state="empty"
                  title={t("notifTemplatesPage.empty")}
                  description={t("notifTemplatesPage.emptyHint")}
                  icon={<BellIcon size={24} />}
                />
              )}
            />
            </>
          ) : (
            <Table.ScrollContainer minWidth={800}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                {thead}
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <DeliveryRowAction label={t("notifTemplatesPage.edit")} icon={<PencilSimpleIcon />} state={manageState} onClick={() => handleEdit(row)} />
                          <DeliveryRowAction label={t("notifTemplatesPage.delete")} icon={<TrashIcon />} state={manageState} onClick={() => setDeleteTarget(row)} />
                        </Group>
                      </Table.Td>
                      <Table.Td><Text ff="monospace" fz="var(--nw-fz-mono)" translate="no">{row.templateKey}</Text></Table.Td>
                      <Table.Td><Text size="sm">{t(`templateEditor.channel.${row.channel}`, { defaultValue: row.channel })}</Text></Table.Td>
                      <Table.Td maw={260}><Text size="sm" lineClamp={1}>{row.subject || "-"}</Text></Table.Td>
                      <Table.Td>
                        <DotPill tone={row.isActive ? "green" : "grey"}>
                          {row.isActive ? t("notifTemplatesPage.active") : t("notifTemplatesPage.inactive")}
                        </DotPill>
                      </Table.Td>
                      <Table.Td><Text size="sm" ff="monospace" style={TNUM}>{dateFormat(row.updatedAt)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </TableFrame>
      </Stack>

      {/* Editor drawer (720), mounted at page root */}
      <TemplateEditorDrawer
        opened={drawerOpened}
        template={editTarget}
        onClose={() => setDrawerOpened(false)}
        onSaved={() => mutate()}
      />

      {/* Delete confirmation (440): this removes the row for good, unlike the archived propose templates */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title={t("notifTemplatesPage.deleteTitle")} size={440}>
        <Stack gap="md">
          <Text size="sm">{t("notifTemplatesPage.deleteConfirm")}</Text>
          {deleteTarget ? <Text ff="monospace" fz="var(--nw-fz-mono)" translate="no">{deleteTarget.templateKey}</Text> : null}
          <ConfirmNote helpKey={`${HELP}.confirm.delete`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>{t("templateForm.close")}</Button>
            <Button onClick={confirmDelete} loading={deleting}>{t("notifTemplatesPage.delete")}</Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
